import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import clinicApi, { MEDIA_BASE_URL } from "../../api/clinicClient";
import PortalLayout from "../layout/PortalLayout";
import { Calendar } from "primereact/calendar";
import { AutoComplete } from "primereact/autocomplete";
import { Dropdown } from "primereact/dropdown";

function DoctorCard({ doctor, onBook }) {
  return (
    <div className="ph-card" style={{ padding: "14px 16px", display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{
          width: 40, height: 40, borderRadius: 10, flexShrink: 0,
          background: "var(--brand-light)", color: "var(--brand-primary)",
          display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18,
          overflow: "hidden",
        }}>
          {doctor.photo_url ? (
            <img src={`${MEDIA_BASE_URL}${doctor.photo_url}`} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          ) : (
            <i className="pi pi-user" />
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14.5, fontWeight: 700, color: "var(--text-primary)" }}>
            Dr. {doctor.name}
          </div>
          {doctor.specialty && (
            <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>{doctor.specialty}</div>
          )}
        </div>
      </div>

      {doctor.bio && (
        <div style={{
          fontSize: 12.5, color: "var(--text-secondary)", lineHeight: 1.5,
          display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden",
        }}>
          {doctor.bio}
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {doctor.clinics.map((c) => (
          <div key={c.affiliation_id} style={{
            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
            padding: "10px 12px", background: "var(--surface-bg)", borderRadius: 10,
          }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {c.clinic_name}
              </div>
              <div style={{ fontSize: 11.5, color: "var(--text-muted)", marginTop: 2 }}>
                Consultation: Rs. {parseFloat(c.consultation_fee).toLocaleString("en-LK", { minimumFractionDigits: 2 })}
                {typeof c.has_availability === "boolean" && (
                  <span style={{ marginLeft: 8, color: c.has_availability ? "#059669" : "#dc2626", fontWeight: 600 }}>
                    {c.has_availability ? "Available" : "No slots"}
                  </span>
                )}
              </div>
              {typeof c.booked_count === "number" && (
                <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
                  {c.appointment_mode === "token"
                    ? `${c.booked_count}/${c.capacity} booked today`
                    : `${c.booked_count} booked · ${c.total_slots ?? 0} slots today`}
                </div>
              )}
            </div>
            <button
              onClick={() => onBook(c.affiliation_id, doctor, c)}
              style={{
                flexShrink: 0, padding: "7px 14px", borderRadius: 8, fontSize: 12.5, fontWeight: 600,
                background: "var(--brand-primary)", color: "white", border: "none", cursor: "pointer", fontFamily: "inherit",
              }}
            >
              Book
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function FindDoctor() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [nameSuggestions, setNameSuggestions] = useState([]);
  const [allNames, setAllNames] = useState([]);
  const [specialty, setSpecialty] = useState("");
  const [clinics, setClinics] = useState([]);
  const [clinicId, setClinicId] = useState(null);
  const [date, setDate] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    // Fetched once — backs the doctor-name autocomplete and the clinic dropdown.
    clinicApi.get("doctors/search/")
      .then(res => setAllNames([...new Set(res.data.map(d => d.name))]))
      .catch(console.error);
    clinicApi.get("clinics/")
      .then(res => setClinics(res.data))
      .catch(console.error);
  }, []);

  const runSearch = (overrides = {}) => {
    setLoading(true);
    setSearched(true);
    const params = {};
    const effectiveQ = "q" in overrides ? overrides.q : q;
    const effectiveClinicId = "clinicId" in overrides ? overrides.clinicId : clinicId;
    if (effectiveQ) params.q = effectiveQ;
    if (specialty) params.specialty = specialty;
    if (effectiveClinicId) params.clinic_id = effectiveClinicId;
    if (date) params.date = date.toISOString().slice(0, 10);
    clinicApi.get("doctors/search/", { params })
      .then(res => setDoctors(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { runSearch(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <PortalLayout>
      <div className="page-header">
        <h1 className="page-title">Find a Doctor</h1>
        <p className="page-subtitle">Search by name, specialty, or clinic</p>
      </div>

      <div className="ph-card" style={{ padding: 14, marginBottom: 16, display: "flex", flexDirection: "column", gap: 10 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10 }}>
          <AutoComplete
            value={q}
            suggestions={nameSuggestions}
            completeMethod={(e) => {
              const query = e.query.toLowerCase();
              setNameSuggestions(allNames.filter(n => n.toLowerCase().includes(query)));
            }}
            onChange={(e) => setQ(e.value)}
            onSelect={(e) => runSearch({ q: e.value })}
            placeholder="Doctor name"
            inputStyle={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid var(--border-color)", fontSize: 13, fontFamily: "inherit" }}
            style={{ width: "100%" }}
          />
          <input
            placeholder="Specialty"
            value={specialty}
            onChange={(e) => setSpecialty(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && runSearch()}
            style={{ padding: "9px 12px", borderRadius: 8, border: "1px solid var(--border-color)", fontSize: 13, fontFamily: "inherit" }}
          />
          <Dropdown
            value={clinicId}
            options={clinics}
            optionLabel="name"
            optionValue="id"
            onChange={(e) => { setClinicId(e.value); runSearch({ clinicId: e.value }); }}
            placeholder="All Clinics"
            showClear
            style={{ width: "100%" }}
          />
          <Calendar
            value={date}
            onChange={(e) => setDate(e.value)}
            placeholder="Date"
            minDate={new Date()}
            showIcon
            dateFormat="dd M yy"
          />
        </div>
        <button
          onClick={() => runSearch()}
          style={{
            alignSelf: "flex-start", padding: "8px 18px", borderRadius: 8, fontSize: 13, fontWeight: 600,
            background: "var(--brand-primary)", color: "white", border: "none", cursor: "pointer", fontFamily: "inherit",
          }}
        >
          <i className="pi pi-search" style={{ fontSize: 11, marginRight: 6 }} />
          Search
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: "48px 20px", color: "var(--text-muted)" }}>
          <i className="pi pi-spin pi-spinner" style={{ fontSize: 28, display: "block", marginBottom: 12, color: "var(--brand-primary)" }} />
          <div style={{ fontSize: 13 }}>Searching…</div>
        </div>
      ) : searched && doctors.length === 0 ? (
        <div style={{ textAlign: "center", padding: "48px 20px", color: "var(--text-muted)" }}>
          <i className="pi pi-search" style={{ fontSize: 32, display: "block", marginBottom: 12 }} />
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 6 }}>No doctors found</div>
          <div style={{ fontSize: 13 }}>Try a different name, specialty, or clinic.</div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {doctors.map((d) => (
            <DoctorCard
              key={d.doctor_id}
              doctor={d}
              onBook={(affiliationId, doctor, c) =>
                navigate(`/book/${affiliationId}`, {
                  state: {
                    doctorName: doctor.name, specialty: doctor.specialty, clinicName: c.clinic_name,
                    fee: c.consultation_fee, facilityFeeLabel: c.facility_fee_label, facilityFeeAmount: c.facility_fee_amount,
                  },
                })
              }
            />
          ))}
        </div>
      )}
    </PortalLayout>
  );
}
